import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import StoreLayout from "@/store/StoreLayout";
import StoreHome from "@/store/StoreHome";
import StoreSearch from "@/store/StoreSearch";
import StoreProduct from "@/store/StoreProduct";
import { LegacyRedirect } from "@/store/seo/LegacyRedirect";
import { CartProvider } from "@/store/cart";
import { CustomerProvider } from "@/store/customer";
import StorePolicies from "@/store/StorePolicies";
import StoreCheckout from "@/store/StoreCheckout";
import StoreOrder from "@/store/StoreOrder";
import StoreAccount, { ResetPasswordPage } from "@/store/StoreAccount";
import StoreSaved from "@/store/StoreSaved";
import StoreNotFound from "@/store/StoreNotFound";
import StoreCart from "@/store/StoreCart";
import { useParams, useLocation } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import {
  appContextFromLocation,
  appHome,
  appLoginPath,
  isAppRoute,
} from "@/lib/appContext";
import { EnvironmentRibbon } from "@/components/EnvironmentRibbon";
import { isStoreHost } from "@/store/nav";
import ReferencePage from "@/store/seo/ReferencePage";
import CategoryPage, { ModelPage, LangGate } from "@/store/seo/CategoryPage";
import { GuideIndexPage, GuidePage } from "@/store/seo/GuidePages";
import { WishlistProvider } from "@/store/wishlist";
import { ConsentProvider } from "@/store/consent";
import { usePwaIdentity } from "@/components/PwaIdentity";

const AppShell = lazy(() => import("@/components/AppShell"));
const Login = lazy(() => import("@/pages/Login"));
const ControlCenter = lazy(() => import("@/pages/ControlCenter"));
const ForgotPassword = lazy(() => import("@/pages/PasswordRecovery").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/PasswordRecovery").then((m) => ({ default: m.ResetPassword })));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const Assistant = lazy(() => import("@/pages/Assistant"));
const Library = lazy(() => import("@/pages/Library"));
const Collaboration = lazy(() => import("@/pages/Collaboration"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Admin = lazy(() => import("@/pages/Admin"));
const Settings = lazy(() => import("@/pages/Settings"));
const Service = lazy(() => import("@/pages/Service"));
const Track = lazy(() => import("@/pages/Track"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const Publishing = lazy(() => import("@/pages/Publishing"));
const Drafts = lazy(() => import("@/pages/Drafts"));
const UnitScan = lazy(() => import("@/pages/UnitScan"));
const Sales = lazy(() => import("@/pages/Sales"));
const Review = lazy(() => import("@/pages/Review"));
const Audit = lazy(() => import("@/pages/Audit"));
const KnowledgeBuilder = lazy(() => import("@/pages/KnowledgeBuilder"));
const BackOffice = lazy(() => import("@/pages/BackOffice"));
const Commerce = lazy(() => import("@/pages/Commerce"));
const TechShell = lazy(() => import("@/tech/TechShell"));
const Ops = lazy(() => import("@/pages/Ops"));
const Capture = lazy(() => import("@/pages/Capture"));
const Scanner = lazy(() => import("@/pages/Scanner"));
const ScannerLookup = lazy(() => import("@/pages/ScannerLookup"));
const ScannerPutaway = lazy(() => import("@/pages/ScannerPutaway"));
const ScannerRelocate = lazy(() => import("@/pages/ScannerRelocate"));
const ScannerPicking = lazy(() => import("@/pages/ScannerPicking"));
const ScannerAdmin = lazy(() => import("@/pages/ScannerAdmin"));
const BoxQrLanding = lazy(() => import("@/pages/BoxQrLanding"));
const ShelfQrLanding = lazy(() => import("@/pages/ShelfQrLanding"));
const TechHome = lazy(() => import("@/tech/TechHome"));
const TechOrder = lazy(() => import("@/tech/TechOrder"));
const TechActivity = lazy(() => import("@/tech/TechPages").then((m) => ({ default: m.TechActivity })));
const TechProfile = lazy(() => import("@/tech/TechPages").then((m) => ({ default: m.TechProfile })));
const TechHelp = lazy(() => import("@/tech/TechPages").then((m) => ({ default: m.TechHelp })));
const TechRoom = lazy(() => import("@/tech/TechRoom"));
const WiringApp = lazy(() => import("@/wiring/WiringApp"));
const CatalogCleanup = lazy(() => import("@/pages/CatalogCleanup"));

function ReceiptRedirect() {
  const { token } = useParams();
  window.location.replace(`${API_BASE}/receipt/${token}`);
  return null;
}

function StoreLegacyRedirect() {
  const loc = useLocation();
  const to = loc.pathname.replace(/^\/store/, "") + loc.search + loc.hash;
  return <Navigate to={to || "/"} replace />;
}

function StoreApp() {
  usePwaIdentity("store");
  return (
    <CartProvider>
      <WishlistProvider>
        <CustomerProvider>
          <StoreLayout />
        </CustomerProvider>
      </WishlistProvider>
    </CartProvider>
  );
}

const StoreShell = (
  <ErrorBoundary>
    <StoreApp />
  </ErrorBoundary>
);

const StoreChildren = (
  <>
    <Route index element={<StoreHome />} />
    <Route path="shop/*" element={<LegacyRedirect />} />
    <Route path="product-category/*" element={<LegacyRedirect />} />
    <Route path="product/*" element={<LegacyRedirect />} />
    <Route
      path="complete-guide-to-buying-washing-machine-replacement-parts"
      element={<LegacyRedirect />}
    />
    <Route path="search" element={<StoreSearch />} />
    <Route path="cart" element={<StoreCart />} />
    <Route
      path="privacy"
      element={<LegacyRedirect to="/policies#privacy" />}
    />
    <Route path="terms" element={<LegacyRedirect to="/policies#terms" />} />
    <Route
      path="shipping"
      element={<LegacyRedirect to="/policies#shipping" />}
    />
    <Route
      path="returns"
      element={<LegacyRedirect to="/policies#returns" />}
    />
    <Route path="p/:slug" element={<StoreProduct />} />
    <Route path="policies" element={<StorePolicies />} />
    <Route path="checkout" element={<StoreCheckout />} />
    <Route path="order/:token" element={<StoreOrder />} />
    <Route path="account" element={<StoreAccount />} />
    <Route path="reset-password" element={<ResetPasswordPage />} />
    <Route path="saved" element={<StoreSaved />} />
    <Route
      path=":lang"
      element={
        <LangGate>
          <StoreHome />
        </LangGate>
      }
    />
    <Route
      path=":lang/parts/:slug"
      element={
        <LangGate>
          <ReferencePage />
        </LangGate>
      }
    />
    <Route
      path=":lang/models/:slug"
      element={
        <LangGate>
          <ModelPage />
        </LangGate>
      }
    />
    <Route
      path=":lang/guides"
      element={
        <LangGate>
          <GuideIndexPage />
        </LangGate>
      }
    />
    <Route
      path=":lang/guias"
      element={
        <LangGate>
          <GuideIndexPage />
        </LangGate>
      }
    />
    <Route
      path=":lang/guides/:slug"
      element={
        <LangGate>
          <GuidePage />
        </LangGate>
      }
    />
    <Route
      path=":lang/guias/:slug"
      element={
        <LangGate>
          <GuidePage />
        </LangGate>
      }
    />
    <Route
      path=":lang/:applSeg"
      element={
        <LangGate>
          <CategoryPage />
        </LangGate>
      }
    />
    <Route
      path=":lang/:applSeg/:seg2"
      element={
        <LangGate>
          <CategoryPage />
        </LangGate>
      }
    />
    <Route
      path=":lang/:applSeg/:seg2/:seg3"
      element={
        <LangGate>
          <CategoryPage />
        </LangGate>
      }
    />
    <Route path="*" element={<StoreNotFound />} />
  </>
);

function SessionLoader() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={
        "flex min-h-screen flex-col items-center justify-center gap-4 bg-black p-6 "
        + "text-center text-yellow-400"
      }
      data-testid="session-loader"
    >
      <div className="ascii-loader font-mono text-sm" />
      {slow && (
        <>
          <p className="font-mono text-xs uppercase text-neutral-400">
            La conexión con el servidor está tardando
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full border border-yellow-400 px-4 py-2 font-mono text-xs uppercase"
            data-testid="session-loader-retry"
          >
            Reintentar
          </button>
        </>
      )}
    </div>
  );
}

function BareShell({ children }) {
  return <>{children}</>;
}

function WorkspaceHome() {
  const { user } = useAuth();
  const researcher = Boolean(
    user?.role === "worker" &&
      (user?.permissions || []).includes("part_research"),
  );
  return researcher ? (
    <Navigate to="/backoffice/investigacion" replace />
  ) : (
    <Dashboard />
  );
}

function Guard({ children, module, roles, perm, bare = false }) {
  const { user, hasModule, logout } = useAuth();
  const Shell = bare ? BareShell : AppShell;
  const hasPerm =
    perm &&
    user &&
    (user.role === "admin" || (user.permissions || []).includes(perm));
  const modOk =
    !module ||
    (Array.isArray(module)
      ? module.some((m) => hasModule(m))
      : hasModule(module));
  const deniedByStandalonePermission = Boolean(
    perm && !module && !roles && !hasPerm,
  );
  if (user === null) return <SessionLoader />;
  if (!user) return <Navigate to={appLoginPath()} replace />;
  if (user.must_change_password && window.location.pathname !== "/settings")
    return <Navigate to="/settings#password" replace />;
  if (
    deniedByStandalonePermission ||
    (!hasPerm && (!modOk || (roles && !roles.includes(user.role))))
  ) {
    return (
      <Shell>
        <div
          className="p-10 text-center font-mono text-xs uppercase text-neutral-500"
          data-testid="module-denied"
        >
          <p>No tienes acceso a esta aplicación.</p>
          <button
            type="button"
            onClick={async () => {
              await logout();
              window.location.assign(appLoginPath());
            }}
            className="mt-4 border border-neutral-500 px-3 py-2 text-[10px] text-neutral-300"
            data-testid="module-denied-return-button"
          >
            Cambiar de cuenta
          </button>
        </div>
      </Shell>
    );
  }
  return (
    <ErrorBoundary>
      <Shell>
        <Suspense fallback={<SessionLoader />}>{children}</Suspense>
      </Shell>
    </ErrorBoundary>
  );
}

function Public({ children }) {
  const { user } = useAuth();
  const context = appContextFromLocation();
  const next = new URLSearchParams(window.location.search).get("next");
  const destination =
    next &&
    next.startsWith("/") &&
    !next.startsWith("//") &&
    isAppRoute(next, context)
      ? next
      : appHome(context);
  if (user) return <Navigate to={destination} replace />;
  return children;
}

function RouteAuth({ children }) {
  const location = useLocation();
  const context = appContextFromLocation({
    ...window.location,
    pathname: location.pathname,
    search: location.search,
  });
  return <AuthProvider key={context}>{children}</AuthProvider>;
}

function App() {
  return (
    <div className="App">
      <ConsentProvider>
        <BrowserRouter>
          <RouteAuth>
            <EnvironmentRibbon />
            <Suspense fallback={<SessionLoader />}>
            {isStoreHost ? (
              <Routes>
                <Route path="/" element={StoreShell}>
                  {StoreChildren}
                </Route>
                <Route path="/store/*" element={<StoreLegacyRedirect />} />
                <Route path="*" element={StoreShell}>
                  <Route path="*" element={<StoreNotFound />} />
                </Route>
              </Routes>
            ) : (
              <Routes>
                <Route
                  path="/login"
                  element={
                    <Public>
                      <Login />
                    </Public>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <Public>
                      <ForgotPassword />
                    </Public>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <Public>
                      <ResetPassword />
                    </Public>
                  }
                />
                <Route path="/track/:token" element={<Track />} />
                <Route path="/payment/success" element={<PaymentResult />} />
                <Route
                  path="/payment/cancel"
                  element={<PaymentResult cancelled />}
                />
                <Route path="/receipt/:token" element={<ReceiptRedirect />} />
                <Route path="/store" element={StoreShell}>
                  {StoreChildren}
                </Route>
                <Route
                  path="/capture"
                  element={
                    <Guard bare module="second_life" perm="mobile_capture">
                      <Capture />
                    </Guard>
                  }
                />
                <Route path="/box/:code" element={<BoxQrLanding />} />
                <Route path="/shelf/:shelfId" element={<ShelfQrLanding />} />
                <Route
                  path="/scanner"
                  element={
                    <Guard bare perm="scanner_access">
                      <Scanner />
                    </Guard>
                  }
                />
                <Route
                  path="/scanner/consulta"
                  element={
                    <Guard bare perm="scanner_access">
                      <ScannerLookup />
                    </Guard>
                  }
                />
                <Route
                  path="/scanner/guardar"
                  element={
                    <Guard bare perm="scanner_access">
                      <ScannerPutaway />
                    </Guard>
                  }
                />
                <Route
                  path="/scanner/cajas"
                  element={
                    <Guard bare perm="scanner_access">
                      <ScannerRelocate />
                    </Guard>
                  }
                />
                <Route
                  path="/scanner/pedidos"
                  element={
                    <Guard bare perm="scanner_access">
                      <ScannerPicking />
                    </Guard>
                  }
                />
                <Route
                  path="/scanner/admin"
                  element={
                    <Guard bare perm="scanner_boxes_manage">
                      <ScannerAdmin />
                    </Guard>
                  }
                />
                <Route
                  path="/scanner/sales"
                  element={<Navigate to="/scanner/pedidos" replace />}
                />
                <Route
                  path="/scanner/boxes"
                  element={<Navigate to="/scanner/cajas" replace />}
                />
                <Route
                  path="/tech"
                  element={
                    <Guard
                      bare
                      module="owlico"
                      roles={["tecnico", "tecnico_senior", "admin"]}
                    >
                      <TechShell />
                    </Guard>
                  }
                >
                  <Route index element={<TechHome />} />
                  <Route path="orders/:id" element={<TechOrder />} />
                  <Route path="help" element={<TechHelp />} />
                  <Route path="rooms/:rid" element={<TechRoom />} />
                  <Route path="activity" element={<TechActivity />} />
                  <Route path="profile" element={<TechProfile />} />
                </Route>
                <Route
                  path="/wiring"
                  element={
                    <Guard
                      bare
                      module={["owlico", "second_life"]}
                      roles={["admin"]}
                    >
                      <WiringApp />
                    </Guard>
                  }
                />
                <Route
                  path="/"
                  element={
                    <Guard>
                      <WorkspaceHome />
                    </Guard>
                  }
                />
                <Route
                  path="/catalog"
                  element={
                    <Guard module="second_life">
                      <Catalog />
                    </Guard>
                  }
                />
                <Route
                  path="/assistant"
                  element={
                    <Guard module="owlico">
                      <Assistant />
                    </Guard>
                  }
                />
                <Route
                  path="/library"
                  element={
                    <Guard module="owlico" perm="library_view">
                      <Library />
                    </Guard>
                  }
                />
                <Route
                  path="/collaboration"
                  element={
                    <Guard module="owlico">
                      <Collaboration />
                    </Guard>
                  }
                />
                <Route
                  path="/service"
                  element={
                    <Guard
                      module="owlico"
                      roles={["admin", "revisor", "tecnico_senior"]}
                    >
                      <Service />
                    </Guard>
                  }
                />
                <Route
                  path="/ops"
                  element={
                    <Guard module="owlico" roles={["admin", "revisor"]}>
                      <Ops />
                    </Guard>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <Guard module="second_life">
                      <Inventory />
                    </Guard>
                  }
                />
                <Route
                  path="/u/:sku"
                  element={
                    <Guard>
                      <UnitScan />
                    </Guard>
                  }
                />
                <Route
                  path="/commerce"
                  element={
                    <Guard module="second_life" perm="sl_publish">
                      <Commerce />
                    </Guard>
                  }
                />
                <Route
                  path="/publishing"
                  element={
                    <Guard module="second_life" perm="publishing_tech_view">
                      <Publishing />
                    </Guard>
                  }
                />
                <Route
                  path="/publishing/drafts"
                  element={
                    <Guard module="second_life">
                      <Drafts />
                    </Guard>
                  }
                />
                <Route
                  path="/review"
                  element={
                    <Guard module="second_life" perm="compat_review">
                      <Review />
                    </Guard>
                  }
                />
                <Route
                  path="/backoffice/*"
                  element={
                    <Guard module="second_life" perm="tasks_view">
                      <BackOffice />
                    </Guard>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <Guard module="second_life">
                      <Audit />
                    </Guard>
                  }
                />
                <Route
                  path="/knowledge-builder"
                  element={
                    <Guard module={["second_life", "owlico"]} perm="kb_view">
                      <KnowledgeBuilder />
                    </Guard>
                  }
                />
                <Route
                  path="/control"
                  element={
                    <Guard roles={["admin", "revisor"]}>
                      <ControlCenter />
                    </Guard>
                  }
                />
                <Route
                  path="/sales"
                  element={
                    <Guard>
                      <Sales />
                    </Guard>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <Guard>
                      <Admin />
                    </Guard>
                  }
                />
                <Route
                  path="/admin/catalog-cleanup"
                  element={
                    <Guard>
                      <CatalogCleanup />
                    </Guard>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <Guard>
                      <Settings />
                    </Guard>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            )}
            </Suspense>
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{ style: { borderRadius: 0 } }}
            />
          </RouteAuth>
        </BrowserRouter>
      </ConsentProvider>
    </div>
  );
}

export default App;
